import { createFileRoute } from '@tanstack/react-router'

import { useAuth } from '@/auth/use-auth'
import { UnbilledReport } from '@/components/billing/unbilled-report'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { TimeReport } from '@/components/time/time-report'
import { WeekView } from '@/components/time/week-view'
import { TeamWeekView } from '@/components/timesheet/team-week-view'
import { TimesheetView } from '@/components/timesheet/timesheet-view'
import { WorkPlanView } from '@/components/timesheet/work-plan-view'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { matching, oneOf, orElse, readSearch, text } from '@/lib/search-params'

const TABS = ['feuille', 'planification', 'saisies', 'equipe', 'rapports', 'a-facturer'] as const
type TimeTab = (typeof TABS)[number]

const SHAPE = {
  tab: orElse(oneOf(TABS), 'saisies' as const),
  /** Feuille de temps : période affichée. */
  periode: oneOf(['semaine', 'jour'] as const),
  /** Feuille de temps : jour de référence, AAAA-MM-JJ, dans le fuseau du propriétaire. */
  jour: matching(/^\d{4}-\d{2}-\d{2}$/),
  /** Feuille ou plan d'un autre membre, ouvert depuis la vue d'équipe (administrateur). */
  membre: text(),
  /** Vue d'équipe et planification : lundi de la semaine affichée. */
  semaine: matching(/^\d{4}-\d{2}-\d{2}$/),
}

export const Route = createFileRoute('/_app/time')({
  validateSearch: (search: Record<string, unknown>) => readSearch(SHAPE, search),
  component: TimePage,
})

function TimePage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { isAdmin } = useAuth()
  // Un lien vers l'onglet Équipe ne vaut que pour un administrateur.
  const tab = search.tab === 'equipe' && !isAdmin ? 'feuille' : search.tab

  return (
    <div className="space-y-5">
      <PageHeader title="Temps" />

      <Tabs
        value={tab}
        onValueChange={(value) => {
          void navigate({ search: { tab: value as TimeTab }, replace: true })
        }}
      >
        <TabsList>
          <TabsTrigger value="feuille">Feuille de temps</TabsTrigger>
          <TabsTrigger value="planification">Planification</TabsTrigger>
          <TabsTrigger value="saisies">Mes saisies</TabsTrigger>
          {isAdmin && <TabsTrigger value="equipe">Équipe</TabsTrigger>}
          <TabsTrigger value="rapports">Rapports</TabsTrigger>
          <TabsTrigger value="a-facturer">À facturer</TabsTrigger>
        </TabsList>

        <TabsContent value="feuille">
          <Frame>
            {search.membre && (
              <p className="mb-3 text-sm">
                <button
                  type="button"
                  className="text-muted-foreground underline-offset-4 hover:underline"
                  onClick={() => {
                    void navigate({ search: { tab: 'equipe', semaine: search.jour } })
                  }}
                >
                  ← Retour à l’équipe
                </button>
              </p>
            )}
            <TimesheetView
              key={search.membre ?? 'moi'}
              mode={search.periode ?? 'semaine'}
              date={search.jour}
              userId={search.membre}
              onNavigate={(next) => {
                void navigate({
                  search: (current) => ({
                    ...current,
                    ...(next.mode ? { periode: next.mode } : {}),
                    ...(next.date ? { jour: next.date } : {}),
                  }),
                })
              }}
            />
          </Frame>
        </TabsContent>
        <TabsContent value="planification">
          <Frame>
            {search.membre && isAdmin && (
              <p className="mb-3 text-sm">
                <button
                  type="button"
                  className="text-muted-foreground underline-offset-4 hover:underline"
                  onClick={() => {
                    void navigate({ search: { tab: 'equipe', semaine: search.semaine } })
                  }}
                >
                  ← Retour à l’équipe
                </button>
              </p>
            )}
            <WorkPlanView
              key={search.membre ?? 'moi'}
              weekStart={search.semaine}
              userId={isAdmin ? search.membre : undefined}
              onWeekChange={(semaine) => {
                void navigate({ search: (current) => ({ ...current, semaine }) })
              }}
            />
          </Frame>
        </TabsContent>
        <TabsContent value="saisies">
          <Frame>
            <WeekView />
          </Frame>
        </TabsContent>
        {isAdmin && (
          <TabsContent value="equipe">
            <Frame>
              <TeamWeekView
                weekStart={search.semaine}
                onWeekChange={(semaine) => {
                  void navigate({ search: { tab: 'equipe', semaine } })
                }}
                onOpenMember={(membre, semaine) => {
                  void navigate({ search: { tab: 'feuille', membre, jour: semaine } })
                }}
                onOpenPlan={(membre, semaine) => {
                  void navigate({ search: { tab: 'planification', membre, semaine } })
                }}
              />
            </Frame>
          </TabsContent>
        )}
        <TabsContent value="rapports">
          <Frame>
            <TimeReport />
          </Frame>
        </TabsContent>
        <TabsContent value="a-facturer">
          <Frame>
            <UnbilledReport />
          </Frame>
        </TabsContent>
      </Tabs>
    </div>
  )
}
